import { useEffect, useMemo, useState } from 'react';
import { ExpertiseType, SubjectModel } from '../domain/SubjectModel';
import {
 cachedSubjectsService,
 qualificationLevelsService,
 qualificationTypesService,
} from '../services';
import { RefSubjectModel } from '../domain/RefSubjectModel';
import { useSelector } from 'react-redux';
import { authSelector } from '../redux/selectors';
import { useLookup } from '@ofqual/portal-core/core/hooks';

export const useRefSubjects = (subjects: SubjectModel[]): RefSubjectModel[] => {
 const [refSubjects, setRefSubjects] = useState<RefSubjectModel[]>([]);
 const { token } = useSelector(authSelector);

 useEffect(() => {
  let isMounted = true;
  let subjectIds: number[] = [];
  subjects.forEach((s) => {
   subjectIds.push(s.subjectId);
  });

  cachedSubjectsService.getIds(subjectIds, { token }).then((response) => {
    if (isMounted) {
      setRefSubjects(response as RefSubjectModel[]);
    }
  });
  
  return () =>{
    isMounted = false;
  }
 }, [subjects, token]);

 return refSubjects;
};

export const useDisplaySubjects = (
 subjects: SubjectModel[],
 refSubjects: RefSubjectModel[],
 expertiseType: ExpertiseType,
) => {
 const [qualTypeLookups, qualLevelLookups] = useLookup(
  qualificationTypesService,
  qualificationLevelsService,
 );

 const matchingSubjects: SubjectModel[] = useMemo(() => [], []);

 
 const displaySubjects = useMemo(
  () =>
   subjects.map((subject) => {
    if (subject.expertiseType & expertiseType) {
     const refSubject = refSubjects?.find((s) => subject.subjectId === s.id);
     const qualificationTypes = qualTypeLookups?.find(
      (qt) => subject.qualificationTypeId === qt.id,
      );
     const qualificationLevels = qualLevelLookups?.filter((q) =>
      subject.qualificationLevels.some((s) => s.levelId === q.id),
     );

     if (
      qualificationTypes === undefined ||
      qualificationLevels === undefined ||
      refSubject === undefined
     )
      return '';

     let subjectWithName = {
      ...subject,
      name: `${refSubject?.subjectTitle ?? refSubject?.ssaDescription2}`,
     };
     matchingSubjects.push(subjectWithName);

     return `${refSubject?.subjectTitle ?? refSubject?.ssaDescription2} 
      (${qualificationTypes?.code === "GceAsLevel" ? "GCE AS/A level" : qualificationTypes?.description} 
      ${qualificationLevels?.map((l) => l.levelDescription)})`;
    }
    return '';
   }),
  [
   subjects,
   refSubjects,
   qualTypeLookups,
   qualLevelLookups,
   expertiseType,
   matchingSubjects,
  ],
 );

 return {
  displaySubjects,
  qualTypeLookups,
  qualLevelLookups,
  matchingSubjects,
 };
};
